/**
 * AS PILHAS — primeiro a entrar, primeiro a sair.
 *
 * Pedido do gestor em 22/09/2026, no lugar da contagem periódica que ele
 * recusou: «ao olhar pilhas diferentes, quando uma pilha zerar isso tem de
 * estar alinhado com o estoque». É a mesma ideia escrita na migração 005 —
 * «pilhas novas não se remontam com pilhas velhas, então o fim de uma pilha é
 * um ponto de contagem exato, não estimado».
 *
 * ── O QUE ISTO COMPRA, E QUE NADA MAIS COMPRA ─────────────────────────────
 *
 * 1. A CONFERÊNCIA DEIXA DE SER TAREFA. O app diz quanto resta da pilha
 *    aberta; o peixeiro está em pé na frente dela e confere OLHANDO. Ele só
 *    fala quando discorda. É a inversão que vale para o app inteiro: o app
 *    fala, o humano corrige.
 *
 * 2. CADA FECHAMENTO PRODUZ UM NÚMERO QUE NADA MAIS PRODUZ, e acumulado ao
 *    longo de um ano ele deixa de ser conferência e vira calibragem — ver
 *    `calibragem()` no fim deste arquivo.
 *
 * 3. CADA QUILO TRATADO GANHA PREÇO. O FIFO diz de qual compra saiu cada
 *    trato, e a compra tem o valor da nota. É literalmente a peça que o
 *    CLAUDE.md lista como faltando para o custo do ciclo — «a regra que liga
 *    quilos tratados ao preço vigente da ração» —, e ela vem sem coleta nova.
 *
 * ── O LIMITE, DITO AQUI PARA NÃO SE PERDER ────────────────────────────────
 *
 * **O FIFO é uma suposição, não uma observação.** Este arquivo afirma que o
 * consumo veio da pilha mais velha porque é essa a prática da fazenda — e não
 * porque alguém mediu. Se o tratador pegar da pilha nova por estar mais perto
 * da porta, o número fica errado, e a diferença no fechamento passa a medir a
 * indisciplina da pilha em vez do galpão.
 *
 * Por isso a diferença no fechamento mistura TRÊS coisas: disciplina de pilha,
 * precisão do trato e perda de verdade. Ela é «diferença no fechamento», e a
 * tela não pode chamá-la de desperdício — mesma natureza do saldo residual do
 * ciclo, que a regra 4 define como diagnóstico e nunca acusação.
 *
 * E no GRANEL isto enfraquece: não há pilha para olhar. Lá sobra o extrato.
 */

/** Uma compra que virou pilha no galpão. */
export interface CompraDePilha {
  id: string
  /** O dia em que chegou. É ele que ordena a fila do FIFO. */
  data: string
  kg: number
  peso_saco_kg: number
  fabricante: string | null
  fornecedor: string | null
  custo_total_brl: number | null
}

/** Tudo que saiu do galpão num dia: tratos, perdas, saídas antigas. */
export interface ConsumoDoDia {
  data: string
  kg: number
}

/**
 * Alguém olhou a pilha e disse o que havia de verdade.
 *
 * `sobrou_kg` zero é o caso comum e o mais barato de registrar: a pilha
 * acabou. Contar zero saco não é contar.
 */
export interface Conferencia {
  remessa_id: string
  data: string
  sobrou_kg: number
}

export type EstadoDaPilha = 'fechada' | 'aberta' | 'na_fila'

export interface Pilha {
  id: string
  data: string
  kg: number
  peso_saco_kg: number
  fabricante: string | null
  fornecedor: string | null
  custo_total_brl: number | null
  estado: EstadoDaPilha
  consumido_kg: number
  restante_kg: number
  /** De 0 a 1 — o que RESTA, porque é a altura da pilha que ele vê. */
  fracao_restante: number
  /** O dia em que zerou: por consumo, ou porque alguém disse que acabou. */
  fechada_em: string | null
  /** Só onde houve conferência: observado − previsto. Negativo = acabou antes. */
  diferenca_kg: number | null
  /** A diferença sobre o tamanho da pilha. */
  diferenca_fracao: number | null
  /** R$ por quilo desta compra. Nulo sem nota — e nota chega depois do fato. */
  preco_kg: number | null
}

export interface Galpao {
  pilhas: Pilha[]
  /**
   * Consumo que não achou pilha nenhuma para sair.
   *
   * É o caso do primeiro mês, e o da especificação escolhida no trato que
   * nunca foi comprada. Fica separado em vez de virar pilha negativa: pilha
   * negativa é uma coisa que não existe no galpão, e desenhar uma seria
   * inventar um objeto para caber num número.
   */
  consumo_sem_pilha_kg: number
}

/** Abaixo disto é resto de ponto flutuante, não ração. */
const MIGALHA_KG = 0.001

/** Ordena por dia e, no mesmo dia, mantém a ordem estável de chegada. */
function porData<T extends { data: string }>(itens: readonly T[]): T[] {
  return itens
    .map((x, i) => ({ x, i }))
    .sort((a, b) => (a.x.data === b.x.data ? a.i - b.i : a.x.data < b.x.data ? -1 : 1))
    .map(({ x }) => x)
}

/**
 * Monta as pilhas: quem já fechou, qual está aberta e quais esperam.
 *
 * ── POR QUE A LINHA DO TEMPO, E NÃO UM TOTAL SÓ ───────────────────────────
 *
 * Bastaria o total consumido para dizer quanto resta HOJE. Não basta para
 * dizer QUANDO cada pilha fechou, que é o que a lista das fechadas mostra, nem
 * para casar uma conferência com o estado da pilha NO DIA em que ela
 * aconteceu. Uma conferência de 12/08 aplicada sobre o saldo de hoje mediria a
 * diferença errada, e mediria com confiança.
 *
 * ── A CONFERÊNCIA VALE PELO FIM DO DIA ────────────────────────────────────
 *
 * Ela entra DEPOIS do consumo do mesmo dia: quem olha a pilha e diz «acabou»
 * olhou depois de tratar, não antes.
 */
export function empilhar(
  compras: readonly CompraDePilha[],
  consumos: readonly ConsumoDoDia[],
  conferencias: readonly Conferencia[],
): Galpao {
  const fila = porData(compras).map((c) => ({
    compra: c,
    // Compra com quilo estragado vira pilha de zero em vez de envenenar a
    // conta inteira com NaN — e aparece na tela como pilha vazia, que é
    // visível, em vez de sumir.
    tamanho: Number.isFinite(c.kg) && c.kg > 0 ? c.kg : 0,
    consumido: 0,
    fechadaEm: null as string | null,
    diferenca: null as number | null,
  }))

  const porRemessa = new Map(fila.map((p) => [p.compra.id, p]))
  const conferidas = porData(conferencias)
  const dias = porData(consumos)

  let semPilha = 0
  let iConferencia = 0

  /** Tira `kg` da fila, da pilha mais velha para a mais nova. */
  function consumir(kg: number, dia: string): void {
    let falta = Number.isFinite(kg) && kg > 0 ? kg : 0
    for (const p of fila) {
      if (falta <= MIGALHA_KG) break
      // Pilha já fechada não recebe mais consumo, inclusive a que foi fechada
      // à mão antes de zerar: dizer que ela acabou é dizer que dela não sai
      // mais nada. Sem isto, o consumo seguinte voltaria a cavar na pilha que
      // o peixeiro acabou de declarar vazia.
      if (p.fechadaEm !== null) continue
      const disponivel = p.tamanho - p.consumido
      if (disponivel <= MIGALHA_KG) continue
      const tira = Math.min(disponivel, falta)
      p.consumido += tira
      falta -= tira
      if (p.tamanho - p.consumido <= MIGALHA_KG) p.fechadaEm = dia
    }
    // O que não achou pilha não vira pilha negativa: fica à parte, e a tela
    // diz que falta lançar compra.
    if (falta > MIGALHA_KG) semPilha += falta
  }

  /** Aplica as conferências até o fim do dia `ate`, inclusive. */
  function conferirAte(ate: string): void {
    while (iConferencia < conferidas.length) {
      const c = conferidas[iConferencia]
      if (c === undefined || c.data > ate) break
      iConferencia += 1
      const p = porRemessa.get(c.remessa_id)
      if (p === undefined) continue
      const sobrou = Number.isFinite(c.sobrou_kg) && c.sobrou_kg > 0 ? c.sobrou_kg : 0
      const previsto = Math.max(0, p.tamanho - p.consumido)
      // OBSERVADO MENOS PREVISTO. Negativo = a pilha acabou antes do que o app
      // contava, que é o caso comum: mais ração saiu da pilha do que os tratos
      // registraram, ou a pilha nunca teve o que a nota dizia.
      p.diferenca = sobrou - previsto
      // O tamanho passa a ser o que se OBSERVOU, porque observação ganha de
      // aritmética. A pilha some da fila do FIFO quando zera.
      p.tamanho = p.consumido + sobrou
      if (sobrou <= MIGALHA_KG) p.fechadaEm = c.data
    }
  }

  for (const d of dias) {
    conferirAte(anterior(d.data))
    consumir(d.kg, d.data)
    conferirAte(d.data)
  }
  // As conferências posteriores ao último dia de consumo — o caso de quem
  // confere a pilha num domingo sem trato.
  conferirAte('9999-12-31')

  // ABERTA É UMA SÓ: a mais velha que ainda tem ração. É a única linha que
  // pede conferência; as outras são estoque parado.
  let jaAchouAberta = false
  const pilhas: Pilha[] = fila.map((p) => {
    const restante = Math.max(0, p.tamanho - p.consumido)
    const vazia = restante <= MIGALHA_KG
    let estado: EstadoDaPilha
    if (vazia) {
      estado = 'fechada'
    } else if (!jaAchouAberta) {
      estado = 'aberta'
      jaAchouAberta = true
    } else {
      estado = 'na_fila'
    }
    const custo = p.compra.custo_total_brl
    return {
      id: p.compra.id,
      data: p.compra.data,
      kg: p.tamanho,
      peso_saco_kg: p.compra.peso_saco_kg,
      fabricante: p.compra.fabricante,
      fornecedor: p.compra.fornecedor,
      custo_total_brl: custo,
      estado,
      consumido_kg: p.consumido,
      restante_kg: restante,
      fracao_restante: p.tamanho > 0 ? restante / p.tamanho : 0,
      fechada_em: p.fechadaEm,
      diferenca_kg: p.diferenca,
      diferenca_fracao:
        p.diferenca !== null && p.tamanho > 0 ? p.diferenca / p.tamanho : null,
      preco_kg:
        custo !== null && Number.isFinite(custo) && p.tamanho > 0 ? custo / p.tamanho : null,
    }
  })

  return { pilhas, consumo_sem_pilha_kg: semPilha }
}

/** O dia anterior, em ISO. Só para separar «antes» de «no mesmo dia». */
function anterior(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`)
  if (Number.isNaN(d.getTime())) return iso
  d.setUTCDate(d.getUTCDate() - 1)
  return d.toISOString().slice(0, 10)
}

/**
 * Por volta de quando a pilha aberta acaba.
 *
 * ESTIMATIVA, e a tela diz isso (regra 8): o ritmo da semana passada não prevê
 * a próxima. Serve para uma pergunta só — «compro esta semana?».
 */
export function quandoAcaba(
  restanteKg: number,
  ritmoKgDia: number,
  hoje: string,
): string | null {
  if (!Number.isFinite(restanteKg) || restanteKg <= 0) return null
  if (!Number.isFinite(ritmoKgDia) || ritmoKgDia <= 0) return null
  const dias = Math.floor(restanteKg / ritmoKgDia)
  const d = new Date(`${hoje}T12:00:00Z`)
  if (Number.isNaN(d.getTime())) return null
  d.setUTCDate(d.getUTCDate() + dias)
  return d.toISOString().slice(0, 10)
}

// ===========================================================================
// A CALIBRAGEM — o que os fechamentos dizem quando se juntam
// ===========================================================================

/**
 * ── POR QUE DUAS MEDIDAS E NÃO UMA ────────────────────────────────────────
 *
 * A diferença de UMA pilha não diz nada: pilha fecha com sobra num mês e com
 * falta no outro, e isso é o mundo. O que diz alguma coisa é separar:
 *
 *   VIÉS       a média das diferenças. Se toda pilha fecha com 5% de sobra,
 *              alguma coisa é sistemática — o saco pesa mais que o parâmetro
 *              diz, ou o trato é relatado por baixo. ISSO SE CONSERTA.
 *
 *   DISPERSÃO  o quanto elas variam entre si. Isso é ruído, e não se conserta.
 *              Perseguir ruído é como se gasta a credibilidade de um número.
 *
 * Um número só, sem separar os dois, faz qualquer pessoa tratar ruído como
 * problema — e depois ignorar o aviso no dia em que ele for real.
 */
export interface Calibragem {
  /** Quantas pilhas fecharam com conferência. */
  pilhas: number
  /** A média das diferenças, em fração da pilha. Nulo sem nenhuma. */
  vies: number | null
  /** O desvio-padrão amostral delas. Nulo com menos de duas. */
  dispersao: number | null
  /** Abaixo disto a tela não mostra o viés: seriam duas pilhas de sorte. */
  confiavel: boolean
}

/**
 * Três é o mínimo para a média não ser anedota.
 *
 * Não é um número da estatística — é o ponto em que parar de mostrar custa
 * menos do que mostrar. Com duas pilhas, «viés de 6%» é uma frase que alguém
 * vai agir em cima, e ela pode ser inteiramente sorte.
 */
export const PILHAS_PARA_CONFIAR = 3

export function calibragem(pilhas: readonly Pilha[]): Calibragem {
  const dif = pilhas
    .map((p) => p.diferenca_fracao)
    .filter((x): x is number => x !== null && Number.isFinite(x))

  if (dif.length === 0) {
    return { pilhas: 0, vies: null, dispersao: null, confiavel: false }
  }

  const vies = dif.reduce((s, x) => s + x, 0) / dif.length
  const dispersao =
    dif.length < 2
      ? null
      : Math.sqrt(dif.reduce((s, x) => s + (x - vies) ** 2, 0) / (dif.length - 1))

  return {
    pilhas: dif.length,
    vies,
    dispersao,
    confiavel: dif.length >= PILHAS_PARA_CONFIAR,
  }
}
